/**
 * Type-only mirror of the manifest registry.
 *
 * Background
 * ----------
 * `buildModuleRoutes()` (see `./routeBuilder.ts`) reads every module's
 * `manifest.ts` at runtime via `import.meta.glob` and emits TanStack
 * route objects with their literal `path` strings widened to `string`
 * — TypeScript cannot preserve the literal when iterating an array.
 *
 * That widening cascades: `routeTree`'s type forgets the literal route
 * paths, which would break every typed call site
 * (`useSearch({ from: '/profile' })`, `navigate({ to: '/peitho' })`,
 * `<Link to="/finance/instances">`).
 *
 * This file restores the literal-path inference by listing the routes
 * one more time, but **purely for types** — the values returned here
 * are never wired into `createRouter()`. Router.ts feeds these into
 * `TYPE_ONLY_ROUTES`, then casts the runtime tree onto that type.
 *
 * Adding a new module
 * -------------------
 * Append one entry to `MODULE_ROUTE_SPECS` below. The entry lists the
 * module's `surface` and its literal `path` strings (copy them verbatim
 * from the manifest). The `component`, `validateSearch`, etc. are
 * irrelevant here — TanStack only needs `path` to widen the tree type.
 *
 * This is the *only* cross-cutting edit a new domain requires; the rest
 * lives entirely inside `src/modules/<name>/`. With it, the T9 macro
 * acceptance for ariadne lands at ~3-5% changes-outside-module instead
 * of the pre-T9.7 ~18%.
 */
import {
  type createRoute,
  type AnyRootRoute,
} from '@tanstack/react-router'
import type { ModuleSurface } from './types'

/**
 * Spec a module's routes for the type-only tree.
 *
 * One call per module; lists every path. `surface` lets the helper sort
 * the entries into the same product / admin buckets the runtime router
 * uses. The returned object is **structurally faked** with `as never`
 * casts — no real TanStack `createRoute()` is called. That keeps the
 * runtime cost to a single object literal per module (a few bytes total
 * across the registry instead of ~5KB of route metadata).
 *
 * The router-level `as unknown as typeof typeOnlyTree` cast in
 * `src/router.ts` re-applies these types onto the runtime tree (which is
 * fully populated by `buildModuleRoutes()`), so the structural fakery is
 * invisible to call-sites.
 */
function specFor<const TPaths extends readonly string[]>(
  _rootRoute: AnyRootRoute,
  surface: ModuleSurface,
  paths: TPaths,
): { surface: ModuleSurface; routes: { [K in keyof TPaths]: ReturnType<typeof createRoute> } } {
  // Build a flat array of `as never`-typed route placeholders. The
  // values are empty objects: bundle cost ≈ 16 bytes/route instead of
  // the ~300 bytes a full `createRoute()` invocation would emit.
  const routes = paths.map(() => ({}) as never) as never
  return { surface, routes }
}

/**
 * THE ONLY EDIT required when adding a module: append one
 * `specFor(rootRoute, '<surface>', ['/path-1', '/path-2'] as const)` call.
 *
 * The `as const` is what preserves literal types — without it, TS widens
 * `['/foo'].includes(...)` to `string[]` and the inference collapses.
 */
function buildSpecs(rootRoute: AnyRootRoute) {
  return [
    // As entradas de PRODUTO (finance, hello, onboarding, news, releases,
    // waitlist, profile, referrals) saíram em 2026-08-24 junto com os
    // módulos. A superfície consumer da SPA foi deletada — o app Cuidari
    // vive no Expo, servido em `/m/*`. Sobraram só as de admin.
    // cockpit — admin
    specFor(rootRoute, 'admin', [
      '/cockpit',
      '/cockpit/cuidari',
      '/cockpit/cuidari/product',
      '/cockpit/cuidari/users',
      '/cockpit/cuidari/users/$userId',
      '/cockpit/cuidari/growth-graph',
      '/cockpit/cuidari/cohorts',
      '/cockpit/domains/$domainSlug/overview',
      '/cockpit/domains/$domainSlug/users',
      '/cockpit/domains/$domainSlug/growth',
      '/cockpit/domains/$domainSlug/cohorts',
      '/cockpit/domains/$domainSlug/surfaces',
      '/cockpit/domains/$domainSlug/quality',
      '/cockpit/groups/$groupId',
      '/cockpit/flows/$flowId',
      '/cockpit/users/$userId',
      '/cockpit/systems',
      '/cockpit/systems/$systemId',
    ] as const),
    // hephaestus — admin (F2-F5 real data: list + detail drill-downs)
    specFor(rootRoute, 'admin', [
      '/hephaestus',
      '/hephaestus/finance',
      '/hephaestus/finance/bills/$billId',
      '/hephaestus/feedback',
      '/hephaestus/feedback/$id',
      '/hephaestus/feedback/clusters/$id',
      '/hephaestus/releases',
      '/hephaestus/releases/$id',
    ] as const),
    // peitho — admin
    specFor(rootRoute, 'admin', [
      '/peitho',
      '/peitho/waitlist',
      '/peitho/referrals',
      '/peitho/referrals/programs/$program_id',
      '/peitho/referrals/tree/$user_id',
    ] as const),
    // pheme — admin
    specFor(rootRoute, 'admin', ['/pheme'] as const),
    // proteus — admin (Tier 4 dashboard 4.3)
    specFor(rootRoute, 'admin', [
      '/proteus',
      '/proteus/schemas',
      '/proteus/schemas/$key',
      '/proteus/users/$userId',
      '/proteus/orgs/$orgId',
      '/proteus/audit',
    ] as const),
    // iris — admin (Tier 2 Dashboard 2.1)
    specFor(rootRoute, 'admin', [
      '/iris',
      '/iris/deliveries',
      '/iris/deliveries/$id',
      '/iris/channels',
      '/iris/users/$userId/preferences',
      '/iris/threads/$id',
    ] as const),
    // kratos — admin
    specFor(rootRoute, 'admin', ['/kratos'] as const),
    // odysseus — admin (T10 E.1 adds /odysseus/onboarding)
    specFor(rootRoute, 'admin', ['/odysseus', '/odysseus/onboarding'] as const),
    // thalia — admin
    specFor(rootRoute, 'admin', ['/thalia'] as const),
    // eunomia-review — admin
    specFor(rootRoute, 'admin', ['/eunomia/review'] as const),
    // kronos — admin (Tier 2 Dashboard 2.2)
    specFor(rootRoute, 'admin', [
      '/kronos',
      '/kronos/pricing',
      '/kronos/subscriptions',
      '/kronos/subscriptions/$id',
      '/kronos/grants',
      '/kronos/users/$userId',
    ] as const),
    // aegis — admin (Tier 1 Dashboard 1.3)
    specFor(rootRoute, 'admin', [
      '/aegis',
      '/aegis/errors',
      '/aegis/errors/$eventId',
      '/aegis/releases',
      '/aegis/decisions/$decisionId',
    ] as const),
    // stentor — admin (Tier 2 Dashboard 2.3)
    specFor(rootRoute, 'admin', [
      '/stentor',
      '/stentor/campaigns',
      '/stentor/campaigns/$id',
      '/stentor/templates',
      '/stentor/templates/$id',
      '/stentor/audiences',
      '/stentor/audiences/$id',
      '/stentor/short-links',
    ] as const),
  ]
}

/**
 * Return type-only routes for the given surface — the array consumed by
 * `router.ts::TYPE_ONLY_ROUTES`. Same shape as `buildModuleRoutes()`,
 * but every route's `path` is a literal string type.
 */
export function typeOnlyModuleRoutes(
  rootRoute: AnyRootRoute,
  surface: ModuleSurface,
) {
  return buildSpecs(rootRoute)
    .filter((s) => s.surface === surface)
    .flatMap((s) => s.routes)
}
